'use strict';

namespace Core {

  export interface RtbPixelArguments {
    type: string;
    area: string|any;
    ids?: string|null;
    destination?: string|null;
    checkin?: string|null;
    checkout?: string|null;
    adults?: number|string;
    children?: number|string;
    class?: string|null;
  }

  export class RtbHouseTrackingService {
    private secretKey = TRPCONFIG.Domain.RTBHouseUIDSecretKey;
    private rtbHouseUrl = TRPCONFIG.Domain.RTBHouseTrackingURL;
    private optionId = TRPCONFIG.Domain.RTBHouseOptionId;

    // tripStates - reverse map based on tripTypes from trip.servise
    protected tripStates = {
      '1': 'flights',
      '2': 'hotels',
      '3': 'transfers',
      '4': 'holidays',
      '5': 'marhaba',
      '6': 'parking',
      '7': 'attractions',
      '8': 'dynamicpackage',
      '9': 'insurance',
      '10': 'multidestHoliday'
    };

    constructor(
      private $window: ng.IWindowService,
      private authService: any,
      private pageStrand: any,
      private regExpRoutes: any,
      private dateUtil: any,
      private tripService: any,
      private cookieService: any
    ) {
    }

    getPixelString (args: RtbPixelArguments):string {
      // Generic Pixel URL. Result Depend from Input agguments.

      let url = `${this.rtbHouseUrl}?type=img${this.optionId}_${args.type}`;
      url += args.ids || args.ids === null ? `_${args.ids}` : '';
      url += `${this.optionId}_custom_type_${args.area}`;
      url += args.destination || args.destination === null ?
            `${this.optionId}_custom_destination_${args.destination}` : '';
      url += args.checkin || args.checkin === null ?
            `${this.optionId}_custom_checkin_${args.checkin}` : '';
      url += args.checkout || args.checkout === null ?
            `${this.optionId}_custom_checkout_${args.checkout}` : '';
      url += args.adults || args.adults === 0 ?
            `${this.optionId}_custom_adults_${args.adults}` : '';
      url += args.children || args.children === 0 ?
            `${this.optionId}_custom_children_${args.children}`: '';
      url += args.class || args.class === null ?
            `${this.optionId}_custom_class_${args.class}` : '';
      url += `${this.optionId}_uid_${this.setUID()}`;

      return url;
    }

    getHomeUrl(): string {
      // Unique case pixel URL. Use only for Home page.

      return String.format(this.rtbHouseUrl
        + `?type=img${this.optionId}_home${this.optionId}_uid_{0}`,
        this.setUID());
    }

    getCategoryUrl(area): string {
      // Unique case pixel URL. Use only for Hotel/Holiday etc. pages.

      return String.format(this.rtbHouseUrl
        + `?type=img${this.optionId}_category2_{0}`
        + `${this.optionId}_uid_{1}`
        + `${this.optionId}_custom_type_{0}`,
        area,
        this.setUID());
    }

    getDefaultUrl(): string {
      // Url use for all other pages.

      return `${this.rtbHouseUrl}?type=img${this.optionId}_uid_${this.setUID()}`;
    }

    matchFromUrl(url: string, matchs: string[]): RegExpMatchArray[] {
      let matchRes: RegExpMatchArray[] = [];

      if (Array.isArray(matchs)) {
        matchs.forEach(item => {
          let exp: RegExp = new RegExp(item);
          let match: RegExpMatchArray | null = url.match(exp);

          if (match !== null) {
            matchRes.push(match);
          }
        });

      }

      return matchRes;
    }

    boolFromMatch(url: string, matches: string[]): boolean {
      let match = this.matchFromUrl(url, matches);
      return match.length > 0;
    }

    getCurrentArea(path: string): string | null {
      // get current area for pages without TripId

      const parsedPath = path.split('/')[0];
      const category = this.regExpRoutes.categoryCases;

      if (this.boolFromMatch(parsedPath, category)) {
        let res = this.matchFromUrl(parsedPath, category);
        return res[0][0];
      }
      if (this.boolFromMatch(parsedPath, this.regExpRoutes.idBasedPageV2)) {
        let res = this.matchFromUrl(parsedPath, this.regExpRoutes.idBasedPageV2);
        let strand = res[0][0].split('-')[0];
        return this.pageStrand.toRoute(strand);
      }
      if (this.boolFromMatch(parsedPath, this.regExpRoutes.idBasedPageV3)) {
        let res = this.matchFromUrl(parsedPath, this.regExpRoutes.idBasedPageV3);
        let strand = res[0][0].split('-')[2];
        return this.pageStrand.toRoute(strand);
      }

      return null;
    }

    getDataFromTrip(trip): object|boolean {
      // parse Trip object

      const {
        FlightsRequest,
        HotelsRequest,
        TripType
      } = trip;

      let data: any = {},
          req;

      if (TripType === this.tripService.tripTypes.Hotel || TripType === this.tripService.tripTypes.Holiday) {
        req = HotelsRequest;

        data.dest = this.getAirportCodes(req.Destination.DestinationAirports);
        data.checkIn = this.formatDate(req.CheckInDate);
        data.checkOut = this.formatDate(req.CheckOutDate);
      } else if(TripType === this.tripService.tripTypes.Flight) {
        req = FlightsRequest;

        data.checkIn = this.formatDate(req.DepartureDate),
        data.checkOut = this.formatDate(req.ReturnDate),
        data.class = req.Cabin;
        data.dest = this.getAirportCodes(req.DestinationAirports);
      } else {
        return false;
      }

      data = {
        ...data,
        area: this.tripStates[TripType],
        searchId: HotelsRequest && HotelsRequest.AvailabilitySearchId,
        adults: req.Rooms ? req.Rooms[0].Adults : 0,
        children: req.Rooms ? req.Rooms[0].ChildAges.length : 0
      };

      return data;
    }

    setUID(): string {
      const user = this.authService.getUser();

      if (user) {
        const email = user.Email || user.Contact && user.Contact.Email;
        let uId;

        if (email && email.length > 0) {
          uId = this.$window.md5(`${email}${this.secretKey}`);
          return uId;
        }

        uId = this.cookieService.get('RTBUID');
        if (uId != null) {
            return uId;
        }

        const host = window.location.hostname;
        const cookieDomain = host.slice(host.indexOf('.'));
        uId = this.$window.md5(`${user.UtmId}${this.secretKey}`);

        this.cookieService.set(
          'RTBUID',
          uId,
          this.dateUtil.addDays(Date.now(), 365),
          cookieDomain
        );

        return uId;
      }

      return 'unknown';
    }

    getIds(searchList: any[], key?: string): string|null  {
      if (!searchList || searchList.length < 1) {
        return null;
      }

      const k = key ? key : 'Id';
      const topIndex = searchList.length > 5 ? 5 : searchList.length;
      let ids: any[] = [];

      this.topHotelId('set', searchList[0][k]);

      for (let i = 0; i < topIndex; i++) {
        ids.push(searchList[i][k]);
      }

      return ids.join(',');
    }

    formatDate(date):string|null {
      if (!date) {
        return null;
      }

      return this.dateUtil.format(date, 'yyyy-MM-dd');
    }

    getAirportCodes(airports):string|null {
      if (!airports) {
        return null;
      }
      if (Array.isArray(airports)) {
        return airports.length > 1 ? airports.join(',') : airports[0];
      }
      return airports;
    }

    topHotelId(method: string = 'get', val?: string): string|null|void {
      if(method === 'set') {
        this.cookieService.set('_RtbTopHotelId', val);
      } else {
        return this.cookieService.get('_RtbTopHotelId') || null;
      }
    }
  }

  angular
    .module('core')
    .service('rtbHouseTrackingService', RtbHouseTrackingService);
}
